import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './ImageUpload.module.css';
import { TextField } from '../TextField';
import { ImageIcon } from '@navikt/aksel-icons';
import { Paragraph } from '../Paragraph';
import { toast } from 'react-toastify';

type ImageUploadProps = {
  setImageAltText: (altText: string) => void;
  setImage: React.Dispatch<React.SetStateAction<File | null>>;
  imageAltText: string;
  existingImageUpload?: string;
  setImagePreview: React.Dispatch<React.SetStateAction<string>>;
  imagePreview: string;
};

export const ImageUpload = ({
  setImageAltText,
  setImage,
  imageAltText,
  setImagePreview,
  imagePreview,
}: ImageUploadProps) => {
  const { t } = useTranslation();
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handlePreviewClick = () => {
    imageInputRef?.current?.click();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const imageFile = e.target.files[0];
    const image = e.target.files[0];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(imageFile.type)) {
      toast.error(t('imageUpload.invalidFileType'), {
        position: 'bottom-right',
      });
      return;
    }

    if (imageFile.size > maxSize) {
      toast.error(t('imageUpload.fileTooLarge'), {
        position: 'bottom-right',
      });
      return;
    }

    setImagePreview(URL.createObjectURL(imageFile));

    setImage(image);
  };

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  return (
    <div className={classes.imageUpload}>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        hidden
        aria-label={t('imageUpload.uploadCoverImage')}
        onChange={(e) => {
          handleImageUpload(e);
        }}
      />

      {!imagePreview && (
        <button
          type="button"
          onClick={handlePreviewClick}
          className={classes.previewImage}
          aria-label={t('imageUpload.uploadCoverImageHere')}
        >
          <ImageIcon aria-hidden="true" height={30} width={30} />
          <Paragraph size="md">{t('imageUpload.uploadCoverImageHere')}</Paragraph>
        </button>
      )}
      {imagePreview && (
        <div className={classes.imagePreviewAndAlt}>
          <img
            src={imagePreview}
            height={175}
            width={300}
            alt={imageAltText || t('imageUpload.coverImagePreview')}
          />
          <div>
            <TextField
              label={t('imageUpload.altText')}
              id="altText"
              onChange={(e) => setImageAltText(e.target.value)}
              type="text"
              name={'imageAltText'}
              value={imageAltText}
              required
            />
          </div>
        </div>
      )}
    </div>
  );
};
